import { EntityGraphDepth, EntityGraphEdge, EntityGraphEdgeKind, EntityGraphNode } from "./EntityGraph.types";

// Each relationship kind reuses the platform's existing singular label key (databehandler, videregivelse,
// etc.) rather than introducing graph-specific duplicates. UsesSystem points at an asset, Supplier/
// SubProcessor at a supplier, and a process's UsesProcessingActivity/LinkedLegalEntity at a processing activity
// and a legal entity, so those borrow the target's noun.
const edgeKindTranslationKeys: Record<EntityGraphEdgeKind, string> = {
    [EntityGraphEdgeKind.UsesSystem]: "asset",
    [EntityGraphEdgeKind.DataProcessor]: "dataProcessor",
    [EntityGraphEdgeKind.Disclosure]: "disclosure",
    [EntityGraphEdgeKind.Source]: "source",
    [EntityGraphEdgeKind.JointDataController]: "jointDataController",
    [EntityGraphEdgeKind.GroupEntity]: "groupEntity",
    [EntityGraphEdgeKind.Supplier]: "supplier",
    [EntityGraphEdgeKind.SubProcessor]: "secondarySupplier",
    [EntityGraphEdgeKind.AssociatedSystem]: "entityGraphAssociatedSystem",
    [EntityGraphEdgeKind.UsesProcessingActivity]: "processingActivity",
    [EntityGraphEdgeKind.LinkedLegalEntity]: "legalEntity",
};

export function edgeKindTranslationKey(kind: EntityGraphEdgeKind): string {
    return edgeKindTranslationKeys[kind];
}

// A system-to-system association is a symmetric peer link: drawn without an arrowhead, placed side by side
// (kept out of the stacking chain depth), and reported in the inspector's own "associated" section rather
// than as a dependency. It never shares a drawn edge with another kind (System->System is unique to it).
export function isAssociationEdge(edge: EntityGraphEdge): boolean {
    return edge.kinds.length > 0 && edge.kinds.every((kind) => kind === EntityGraphEdgeKind.AssociatedSystem);
}

export const nodeWidth = 158;
export const nodeHeight = 68;
// The mockup used 50px tall cards with a 132px row pitch (82px clear gap). The cards grew to 68px to fit the
// risk chip, so the pitch grows with them — otherwise long horizontal edges flatten into the shrunken gap.
const rowGap = 150;
const columnGap = 34;
const topPad = 40;
const minWorldWidth = 940;

export const minScale = 0.2;
export const maxScale = 2.6;
const fitPadding = 56;
const maxFitScale = 1.1;

export interface GraphAdjacency {
    out: Map<string, Array<string>>;
    in: Map<string, Array<string>>;
}

export function buildAdjacency(nodes: Array<EntityGraphNode>, edges: Array<EntityGraphEdge>): GraphAdjacency {
    const out = new Map<string, Array<string>>();
    const inbound = new Map<string, Array<string>>();
    nodes.forEach((node) => {
        out.set(node.key, []);
        inbound.set(node.key, []);
    });
    edges.forEach((edge) => {
        out.get(edge.fromKey)?.push(edge.toKey);
        inbound.get(edge.toKey)?.push(edge.fromKey);
        // A system association is symmetric, so it is exposed in both directions. This lets an associated
        // system's full ego-network be reached at the next hop (its own suppliers AND the activities that use
        // it), rather than only the half that matches the edge's stored orientation.
        if (isAssociationEdge(edge)) {
            out.get(edge.toKey)?.push(edge.fromKey);
            inbound.get(edge.fromKey)?.push(edge.toKey);
        }
    });
    return { out, in: inbound };
}

// Signed hop levels from the root: negative = upstream (depends on the root, drawn above), positive =
// downstream (what the root depends on, drawn below). Feeds the depth filter only (magnitude), not the axis
// placement (that is layeredLevels). The upstream and downstream sweeps are independent — each with its own
// visited set — so a node reachable in one direction never blocks the other sweep from exploring it. That
// matters for bidirectional association edges: reaching an associated system upstream must not stop the
// downstream sweep from continuing through it to that system's own suppliers. A node reached both ways keeps
// the smaller distance (ties: upstream).
export function levelsFrom(rootKey: string, adjacency: GraphAdjacency): Map<string, number> {
    const distancesFrom = (neighbours: Map<string, Array<string>>): Map<string, number> => {
        const distances = new Map<string, number>([[rootKey, 0]]);
        let frontier = [rootKey];
        let step = 0;
        while (frontier.length > 0) {
            step++;
            const next: Array<string> = [];
            frontier.forEach((key) => {
                neighbours.get(key)?.forEach((neighbour) => {
                    if (!distances.has(neighbour)) {
                        distances.set(neighbour, step);
                        next.push(neighbour);
                    }
                });
            });
            frontier = next;
        }
        return distances;
    };

    const levels = new Map<string, number>([[rootKey, 0]]);
    distancesFrom(adjacency.in).forEach((distance, key) => key !== rootKey && levels.set(key, -distance));
    distancesFrom(adjacency.out).forEach((distance, key) => {
        const existing = levels.get(key);
        if (key !== rootKey && (existing === undefined || Math.abs(existing) > distance)) {
            levels.set(key, distance);
        }
    });
    return levels;
}

// Full transitive closure in one direction with hop distances (excludes the start node itself). Feeds the
// inspector lists and is deliberately NOT depth-capped.
export function fullClosure(startKey: string, neighbours: Map<string, Array<string>>): Map<string, number> {
    const seen = new Map<string, number>();
    // Index cursor instead of queue.shift(): shift() is O(n) per dequeue (it reindexes the whole array), which
    // makes this hot helper O(n²). fullClosure runs for blast radius, dependencies, twice in declaredOnlyKeys,
    // and once per hover in pathHighlight, so the cursor matters.
    const queue: Array<[string, number]> = (neighbours.get(startKey) ?? []).map((key) => [key, 1]);
    let head = 0;
    while (head < queue.length) {
        const [key, distance] = queue[head++];
        if (seen.has(key)) {
            continue;
        }
        seen.set(key, distance);
        neighbours.get(key)?.forEach((neighbour) => {
            if (!seen.has(neighbour)) {
                queue.push([neighbour, distance + 1]);
            }
        });
    }
    return seen;
}

// Nodes related to the root only through declared (dashed) edges: everything the API returned that a
// traversal over solid edges alone cannot reach. Mirrors the closure semantics (independent upstream and
// downstream passes, no mixed-direction paths) and is computed on the full graph rather than the visible
// depth, so a node doesn't flip between confirmed and declared-only when the depth switch changes.
export function declaredOnlyKeys(rootKey: string, nodes: Array<EntityGraphNode>, edges: Array<EntityGraphEdge>): Set<string> {
    const solidAdjacency = buildAdjacency(
        nodes,
        edges.filter((edge) => !edge.dashed)
    );
    const solidReachable = new Set([rootKey, ...fullClosure(rootKey, solidAdjacency.in).keys(), ...fullClosure(rootKey, solidAdjacency.out).keys()]);
    return new Set(nodes.map((node) => node.key).filter((key) => !solidReachable.has(key)));
}

// Rows follow the flow of the domain rather than distance from the root: each row is one stratum of the
// consumer-supplied typeOrder (processes above processing activities above assets above legal entities),
// refined within a type by the longest same-type dependency chain so a sub-processor always sits below the
// vendor that uses it. Strata that nobody in the closure occupies leave no empty row, and levels are re-signed
// relative to the root's stratum so the axis semantics hold: dependents above, dependencies below. Every edge
// kind flows from a lower typeOrder to a higher-or-equal one, so edges always point downward — except
// same-type cycles, whose nodes fall back to chain depth 0 mid-walk and whose lateral edges fall back to
// edgePath's same-row arc.
export function layeredLevels(
    rootKey: string,
    visibleKeys: Set<string>,
    nodesByKey: Map<string, EntityGraphNode>,
    adjacency: GraphAdjacency
): Map<string, number> {
    const depthMemo = new Map<string, number>();
    const onStack = new Set<string>();

    const sameTypeChainDepth = (key: string): number => {
        if (depthMemo.has(key)) {
            return depthMemo.get(key)!;
        }
        if (onStack.has(key)) {
            return -1;
        }
        onStack.add(key);
        let depth = 0;
        adjacency.in.get(key)?.forEach((dependent) => {
            if (visibleKeys.has(dependent) && nodesByKey.get(dependent)!.typeOrder === nodesByKey.get(key)!.typeOrder) {
                depth = Math.max(depth, sameTypeChainDepth(dependent) + 1);
            }
        });
        onStack.delete(key);
        depthMemo.set(key, depth);
        return depth;
    };

    const stratumByKey = new Map<string, string>();
    visibleKeys.forEach((key) => stratumByKey.set(key, `${nodesByKey.get(key)!.typeOrder}:${sameTypeChainDepth(key)}`));

    const occupiedStrata = [...new Set(stratumByKey.values())].map((stratum) => stratum.split(":").map(Number)).sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    const rowIndexByStratum = new Map(occupiedStrata.map((stratum, index) => [stratum.join(":"), index]));

    const rootRow = rowIndexByStratum.get(stratumByKey.get(rootKey)!)!;
    const result = new Map<string, number>();
    visibleKeys.forEach((key) => result.set(key, rowIndexByStratum.get(stratumByKey.get(key)!)! - rootRow));
    return result;
}

export function filterByDepth(levels: Map<string, number>, depth: EntityGraphDepth): Set<string> {
    const maxDepth = depth === "all" ? Number.POSITIVE_INFINITY : depth;
    return new Set([...levels.entries()].filter(([, level]) => Math.abs(level) <= maxDepth).map(([key]) => key));
}

export interface NodePosition {
    x: number;
    y: number;
}

// Places ordered nodes at their desired centres subject to a minimum centre-to-centre gap, with least-squares
// (pool-adjacent-violators) so a block of nodes centres on its targets without any left/right bias. The gap
// constraint x[i] >= x[i-1] + gap becomes a plain non-decreasing constraint after substituting y[i] = x[i] -
// i*gap, which is exactly isotonic regression: a run of targets that would overlap collapses to their mean,
// so e.g. three children all wanting their parent's column spread out evenly centred on it.
function resolveRow(desired: Array<number>, minGap: number): Array<number> {
    const targets = desired.map((value, index) => value - index * minGap);
    const blocks: Array<{ mean: number; count: number }> = [];
    targets.forEach((target) => {
        let block = { mean: target, count: 1 };
        while (blocks.length > 0 && blocks[blocks.length - 1].mean > block.mean) {
            const previous = blocks.pop()!;
            const count = previous.count + block.count;
            block = { mean: (previous.mean * previous.count + block.mean * block.count) / count, count };
        }
        blocks.push(block);
    });
    const result: Array<number> = [];
    blocks.forEach((block) => {
        for (let i = 0; i < block.count; i++) {
            result.push(block.mean + result.length * minGap);
        }
    });
    return result;
}

// Layered layout: one row per signed level, initially sorted by type-order then label so ties stay stable,
// then reordered by barycenter sweeps (average x of each node's cross-row neighbours, top-to-bottom then
// bottom-to-top) to minimise edge crossings. Once the order is fixed, a coordinate-assignment phase gives each
// node an actual x pulled to the average of its parent/child columns and resolves overlaps per row
// (resolveRow), so a node sits centred under/over its family — a single-parent node's children land centred on
// it, and a shared node centres on the average of its parents. The viewport pans/zooms over the result.
export function layoutGraph(
    visibleKeys: Set<string>,
    levels: Map<string, number>,
    nodesByKey: Map<string, EntityGraphNode>,
    edges: Array<EntityGraphEdge>
): Map<string, NodePosition> {
    const byLevel = new Map<number, Array<string>>();
    visibleKeys.forEach((key) => {
        const level = levels.get(key)!;
        byLevel.set(level, [...(byLevel.get(level) ?? []), key]);
    });

    const levelKeys = [...byLevel.keys()].sort((a, b) => a - b);
    if (levelKeys.length === 0) {
        return new Map();
    }
    const minLevel = levelKeys[0];

    levelKeys.forEach((level) =>
        byLevel.get(level)!.sort((a, b) => {
            const nodeA = nodesByKey.get(a)!;
            const nodeB = nodesByKey.get(b)!;
            return nodeA.typeOrder - nodeB.typeOrder || nodeA.label.localeCompare(nodeB.label);
        })
    );

    const widestRow = Math.max(...levelKeys.map((level) => byLevel.get(level)!.length));
    const worldWidth = Math.max(minWorldWidth, widestRow * (nodeWidth + columnGap) + 200);

    const positions = new Map<string, NodePosition>();
    const placeRow = (level: number) => {
        const row = byLevel.get(level)!;
        const totalWidth = row.length * nodeWidth + (row.length - 1) * columnGap;
        const startX = (worldWidth - totalWidth) / 2;
        const y = topPad + (level - minLevel) * rowGap;
        row.forEach((key, index) => positions.set(key, { x: startX + index * (nodeWidth + columnGap), y }));
    };
    levelKeys.forEach(placeRow);

    const neighboursByKey = new Map<string, Array<string>>();
    edges
        .filter((edge) => visibleKeys.has(edge.fromKey) && visibleKeys.has(edge.toKey))
        .forEach((edge) => {
            neighboursByKey.set(edge.fromKey, [...(neighboursByKey.get(edge.fromKey) ?? []), edge.toKey]);
            neighboursByKey.set(edge.toKey, [...(neighboursByKey.get(edge.toKey) ?? []), edge.fromKey]);
        });

    const centerX = (key: string) => positions.get(key)!.x + nodeWidth / 2;
    // Same-row neighbours (lateral edges) are excluded — a node must not drag itself around via its own row.
    const barycenter = (key: string, level: number) => {
        const neighbourXs = (neighboursByKey.get(key) ?? []).filter((neighbour) => levels.get(neighbour) !== level).map(centerX);
        return neighbourXs.length === 0 ? centerX(key) : neighbourXs.reduce((sum, x) => sum + x, 0) / neighbourXs.length;
    };
    const sweep = (order: Array<number>) =>
        order.forEach((level) => {
            const row = byLevel.get(level)!;
            const scores = new Map(row.map((key) => [key, barycenter(key, level)]));
            row.sort((a, b) => scores.get(a)! - scores.get(b)!);
            placeRow(level);
        });
    const sweepPasses = 3;
    for (let pass = 0; pass < sweepPasses; pass++) {
        sweep(levelKeys);
        sweep([...levelKeys].reverse());
    }

    // Coordinate-assignment phase: the order is now fixed; give each node an x pulled to the average of its
    // cross-row neighbours and resolve overlaps per row. Sweeping down then up several times lets parent and
    // child columns settle onto each other so families line up vertically.
    const minCenterGap = nodeWidth + columnGap;
    const assignX = (level: number) => {
        const row = byLevel.get(level)!;
        const desired = row.map((key) => barycenter(key, level));
        const centers = resolveRow(desired, minCenterGap);
        const y = topPad + (level - minLevel) * rowGap;
        row.forEach((key, index) => positions.set(key, { x: centers[index] - nodeWidth / 2, y }));
    };
    const coordinatePasses = 4;
    for (let pass = 0; pass < coordinatePasses; pass++) {
        levelKeys.forEach(assignX);
        [...levelKeys].reverse().forEach(assignX);
    }
    return positions;
}

export interface PathHighlight {
    nodeKeys: Set<string>;
    edgeKeys: Set<string>;
}

// Everything lying on a directed path between the hovered node and the root, in either direction — but only
// over confirmed (solid) edges: a dashed edge means the relationship is not part of the focused entity's
// chain, so a route crossing one is not a connection the graph should assert. A node is on a hovered->root
// path exactly when it is both a descendant of the hovered node and an ancestor of the root, and an edge is
// on such a path exactly when both its endpoints are — so two closures per direction answer the whole
// question. A hovered card with no confirmed route (a declared-only bystander) falls back to just its own
// direct relations, anchoring it in the picture without claiming it reaches the focus. Edge keys match
// GraphEdgeSegment.key.
export function pathHighlight(hoveredKey: string, rootKey: string, edges: Array<EntityGraphEdge>): PathHighlight {
    const solidEdges = edges.filter((edge) => !edge.dashed);
    const out = new Map<string, Array<string>>();
    const inbound = new Map<string, Array<string>>();
    solidEdges.forEach((edge) => {
        out.set(edge.fromKey, [...(out.get(edge.fromKey) ?? []), edge.toKey]);
        inbound.set(edge.toKey, [...(inbound.get(edge.toKey) ?? []), edge.fromKey]);
    });

    const reachable = (startKey: string, neighbours: Map<string, Array<string>>) => new Set([startKey, ...fullClosure(startKey, neighbours).keys()]);
    const intersect = (a: Set<string>, b: Set<string>) => new Set([...a].filter((key) => b.has(key)));

    const towardRoot = intersect(reachable(hoveredKey, out), reachable(rootKey, inbound));
    const fromRoot = intersect(reachable(rootKey, out), reachable(hoveredKey, inbound));

    const nodeKeys = new Set([...towardRoot, ...fromRoot]);
    if (!nodeKeys.has(hoveredKey)) {
        const incident = edges.filter((edge) => edge.fromKey === hoveredKey || edge.toKey === hoveredKey);
        return {
            nodeKeys: new Set([hoveredKey, ...incident.flatMap((edge) => [edge.fromKey, edge.toKey])]),
            edgeKeys: new Set(incident.map((edge) => `${edge.fromKey}->${edge.toKey}`)),
        };
    }

    const onPath = (edge: EntityGraphEdge, pathNodes: Set<string>) => pathNodes.has(edge.fromKey) && pathNodes.has(edge.toKey);
    return {
        nodeKeys,
        edgeKeys: new Set(solidEdges.filter((edge) => onPath(edge, towardRoot) || onPath(edge, fromRoot)).map((edge) => `${edge.fromKey}->${edge.toKey}`)),
    };
}

const edgeClearance = 14;
const bendPadding = 12;

// Nearest x to desiredX that clears every card in the given rows by edgeClearance — skip-level edges travel
// vertically through this free column gap. Beyond the outermost cards everything is free, so a bend always exists.
function freeBendX(desiredX: number, rowYs: Set<number>, positions: Map<string, NodePosition>): number {
    const intervals = [...positions.values()]
        .filter((position) => rowYs.has(position.y))
        .map((position): [number, number] => [position.x - edgeClearance, position.x + nodeWidth + edgeClearance])
        .sort((a, b) => a[0] - b[0]);

    const merged: Array<[number, number]> = [];
    intervals.forEach(([start, end]) => {
        const last = merged[merged.length - 1];
        if (last && start <= last[1]) {
            last[1] = Math.max(last[1], end);
        } else {
            merged.push([start, end]);
        }
    });

    const blocking = merged.find(([start, end]) => desiredX > start && desiredX < end);
    if (!blocking) {
        return desiredX;
    }
    return desiredX - blocking[0] < blocking[1] - desiredX ? blocking[0] : blocking[1];
}

// The mockup only ever drew source-bottom → target-top curves; its BFS levels guaranteed every edge spanned
// exactly one row, so nothing could ever sit in an edge's way. Longest-chain layering breaks both guarantees:
// same-row edges arc above the row, and edges spanning multiple rows dodge the rows in between by curving to
// a free column gap (freeBendX), running vertically through it, and curving back into the target — the curved
// parts stay inside the card-free bands between rows.
export function edgePath(from: NodePosition, to: NodePosition, positions: Map<string, NodePosition>): string {
    const x1 = from.x + nodeWidth / 2;
    const x2 = to.x + nodeWidth / 2;

    if (to.y === from.y) {
        const y = from.y;
        const arcHeight = Math.min(64, 36 + Math.abs(x2 - x1) * 0.05);
        return `M ${x1} ${y} C ${x1} ${y - arcHeight}, ${x2} ${y - arcHeight}, ${x2} ${y}`;
    }

    const downward = to.y > from.y;
    const startY = downward ? from.y + nodeHeight : from.y;
    const endY = downward ? to.y : to.y + nodeHeight;

    const betweenRowYs = new Set(
        [...positions.values()].map((position) => position.y).filter((y) => (downward ? y > from.y && y < to.y : y > to.y && y < from.y))
    );
    if (betweenRowYs.size === 0) {
        const middleY = (startY + endY) / 2;
        return `M ${x1} ${startY} C ${x1} ${middleY}, ${x2} ${middleY}, ${x2} ${endY}`;
    }

    const bendX = freeBendX((x1 + x2) / 2, betweenRowYs, positions);
    const rowYList = [...betweenRowYs];
    const nearY = downward ? Math.min(...rowYList) - bendPadding : Math.max(...rowYList) + nodeHeight + bendPadding;
    const farY = downward ? Math.max(...rowYList) + nodeHeight + bendPadding : Math.min(...rowYList) - bendPadding;
    const middleEnter = (startY + nearY) / 2;
    const middleExit = (farY + endY) / 2;
    return (
        `M ${x1} ${startY} C ${x1} ${middleEnter}, ${bendX} ${middleEnter}, ${bendX} ${nearY} ` +
        `L ${bendX} ${farY} C ${bendX} ${middleExit}, ${x2} ${middleExit}, ${x2} ${endY}`
    );
}

// An edge points into the blast radius when its target sits at or above the root's row and closer to it.
export function isUpEdge(sourceLevel: number, targetLevel: number): boolean {
    return targetLevel < sourceLevel && targetLevel <= 0;
}

export interface ViewTransform {
    scale: number;
    tx: number;
    ty: number;
}

export function clampScale(scale: number): number {
    return Math.max(minScale, Math.min(maxScale, scale));
}

export function zoomAtPoint(transform: ViewTransform, x: number, y: number, factor: number): ViewTransform {
    const newScale = clampScale(transform.scale * factor);
    return {
        scale: newScale,
        tx: x - (x - transform.tx) * (newScale / transform.scale),
        ty: y - (y - transform.ty) * (newScale / transform.scale),
    };
}

export interface GraphBoundingBox {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
}

// Padded for the over-appetite badge above the cards and the FOCUS tag below them.
export function graphBoundingBox(positions: Map<string, NodePosition>): GraphBoundingBox | null {
    if (positions.size === 0) {
        return null;
    }
    const points = [...positions.values()];
    return {
        minX: Math.min(...points.map((point) => point.x)) - 12,
        maxX: Math.max(...points.map((point) => point.x)) + nodeWidth + 12,
        minY: Math.min(...points.map((point) => point.y)) - 16,
        maxY: Math.max(...points.map((point) => point.y)) + nodeHeight + 16,
    };
}

export function fitToView(boundingBox: GraphBoundingBox, viewportWidth: number, viewportHeight: number): ViewTransform {
    const boxWidth = Math.max(1, boundingBox.maxX - boundingBox.minX);
    const boxHeight = Math.max(1, boundingBox.maxY - boundingBox.minY);
    const scale = clampScale(Math.min((viewportWidth - fitPadding) / boxWidth, (viewportHeight - fitPadding) / boxHeight, maxFitScale));
    return {
        scale,
        tx: (viewportWidth - boxWidth * scale) / 2 - boundingBox.minX * scale,
        ty: (viewportHeight - boxHeight * scale) / 2 - boundingBox.minY * scale,
    };
}

// Free-text sub-processor links are stored without a scheme more often than not; same normalization as the
// system suppliers tab's onNameClick.
export function externalLinkUrl(link: string): string {
    return link.startsWith("http://") || link.startsWith("https://") ? link : `https://${link}`;
}
